import mapboxgl from 'mapbox-gl';
import { START_ICON_SIZE } from './layers';

// The measured point is a red version of the start: the start is the planner's
// `hike-control-point-start` sprite (a green ring round white, 164 px at @2x, so
// 82 px at icon-size 1) with the route-start dot in its middle; this is the same
// ring in the sprite's end red, drawn in the DOM so it can carry the label pill,
// be dragged and take keyboard focus.

/** Diameter in px: the start sprite's 82 px at its START_ICON_SIZE, so the two ends match. */
export const END_MARKER_SIZE = Math.round(82 * START_ICON_SIZE);
/** The sprite's ring is ~12% of its width. */
export const END_RING_WIDTH = Math.round(END_MARKER_SIZE * 0.12 * 4) / 4;
/** The centre dot, as the route-start dot drawn inside the start (radius 6). */
export const END_DOT_SIZE = 12;
/** The red of the planner's end and middle control points. */
export const END_RED = '#DB2B35';
/** The label pill's centre sits this far above the point. */
export const LABEL_ABOVE_POINT = 34;
/** The marker grows about its centre while it is dragged. */
export const DRAG_SCALE = 1.18;

export interface TargetMarkerHandlers {
  onDragStart: () => void;
  onDrag: (lngLat: { lng: number; lat: number }) => void;
  onDragEnd: () => void;
  onKeyDown: (event: KeyboardEvent) => void;
}

/** The draggable measured point: red ring, label pill and keyboard focus in one map marker. */
export class MeasureTargetMarker {
  private readonly marker: any;
  private readonly ring: HTMLElement;
  private readonly pill: HTMLElement;
  readonly element: HTMLElement;

  constructor(private readonly map: any, handlers: TargetMarkerHandlers, ariaLabel: string) {
    this.element = document.createElement('div');
    this.element.className = 'measure-target';
    this.element.tabIndex = 0;
    this.element.setAttribute('role', 'slider');
    this.element.setAttribute('aria-label', ariaLabel);
    Object.assign(this.element.style, { width: `${END_MARKER_SIZE}px`, height: `${END_MARKER_SIZE}px`, cursor: 'grab' });

    this.ring = document.createElement('div');
    this.ring.className = 'measure-target-dot';
    Object.assign(this.ring.style, {
      width: '100%', height: '100%', boxSizing: 'border-box', borderRadius: '50%',
      border: `${END_RING_WIDTH}px solid ${END_RED}`, background: '#FFFFFF',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      pointerEvents: 'none', transition: 'transform .12s ease',
    });
    const dot = document.createElement('div');
    Object.assign(dot.style, { width: `${END_DOT_SIZE}px`, height: `${END_DOT_SIZE}px`, borderRadius: '50%', background: END_RED });
    this.ring.append(dot);

    this.pill = document.createElement('div');
    this.pill.className = 'measure-target-pill';
    this.pill.setAttribute('aria-hidden', 'true');
    this.pill.style.top = `${END_MARKER_SIZE / 2 - LABEL_ABOVE_POINT - 13}px`;

    this.element.append(this.pill, this.ring);
    this.element.addEventListener('keydown', handlers.onKeyDown);

    this.marker = new mapboxgl.Marker({ element: this.element, anchor: 'center', draggable: true });
    this.marker.on('dragstart', () => { this.setDragging(true); handlers.onDragStart(); });
    this.marker.on('drag', () => handlers.onDrag(this.marker.getLngLat()));
    this.marker.on('dragend', () => { this.setDragging(false); handlers.onDragEnd(); });
  }

  show(lngLat: { lng: number; lat: number }): void {
    this.marker.setLngLat([lngLat.lng, lngLat.lat]);
    if (!this.marker._map) this.marker.addTo(this.map);
  }

  setLabel(text: string): void {
    this.pill.textContent = text;
    this.element.setAttribute('aria-valuetext', text);
  }

  setDragging(dragging: boolean): void {
    this.ring.style.transform = dragging ? `scale(${DRAG_SCALE})` : '';
    this.element.style.cursor = dragging ? 'grabbing' : 'grab';
  }

  focus(): void {
    this.element.focus();
  }

  remove(): void {
    this.marker.remove();
  }
}
